import { useCallback, useEffect, useRef, useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getCheckoutContextState,
  getCheckoutEntryPath,
  getCheckoutTrackingProperties,
} from '@monorepo/client-common';
import { PagePath } from '@monorepo/client-types';
import {
  CartEntity,
  CartStep,
  CheckoutEntryPath,
  EventTypes,
  OptionalAssets,
  SupportedCurrencies,
} from '@monorepo/types';
import { useCurrencies } from '@hub/api/queries';
import { useStore } from '@hub/helpers/use-store';
import { getBackClickStepProperties } from '../utils/cart-tracking-utils';
import {
  getDefaultStep,
  isBillingStep,
  PURCHASE_ASSET_PARAM,
  PURCHASE_GROUP_PARAM,
  PURCHASE_STEP_PARAM,
  stepRequiresAsset,
  urlParamToAsset,
  urlParamToStep,
  validateQuickBuy,
} from '../utils/step-url-utils';
import { useCartStep } from './use-cart-step';
import { usePurchaseEvents } from './use-purchase-events';
import { type PurchaseFormData } from './use-purchase-form';

const stepToEventName: Record<CartStep, EventTypes> = {
  [CartStep.Assets]: EventTypes.CHECKOUT_ASSET_SELECTED,
  [CartStep.Programs]: EventTypes.CHECKOUT_GROUP_SELECTED,
  [CartStep.Billing]: EventTypes.CHECKOUT_CLICKED_PROCEED_TO_BILLING,
};

const getEntryEventName = (): EventTypes => {
  const entryPath = getCheckoutEntryPath();
  if (
    entryPath === CheckoutEntryPath.ResumeExplicit ||
    entryPath === CheckoutEntryPath.ResumeImplicit
  ) {
    return EventTypes.CHECKOUT_RESUME_CLICKED;
  }
  return EventTypes.CHECKOUT_ORDER_CLICKED;
};

const backSteps = {
  [CartStep.Assets]: CartStep.Assets,
  [CartStep.Programs]: CartStep.Assets,
  [CartStep.Billing]: CartStep.Programs,
};

const BACK_CLICK_DEBOUNCE_MS = 300;

export const usePurchaseStep = (
  cart: CartEntity | undefined,
  form: UseFormReturn<PurchaseFormData>,
  isCartLoading = false,
) => {
  const { updateStep, isLoading } = useCartStep();
  const { handlePurchaseEvent } = usePurchaseEvents();
  const { data: currencies } = useCurrencies();
  const {
    dataStore: { userStore },
  } = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isBackClickInProgress = useRef(false);
  const backClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (backClickTimeoutRef.current) {
        clearTimeout(backClickTimeoutRef.current);
      }
    };
  }, []);

  const getStepFromUrl = useCallback((): CartStep => {
    const urlStep = searchParams.get(PURCHASE_STEP_PARAM);
    const stepFromUrl = urlParamToStep(urlStep);
    return stepFromUrl || getDefaultStep();
  }, [searchParams]);

  const getAssetFromUrl = useCallback((): OptionalAssets | undefined => {
    const urlAsset = searchParams.get(PURCHASE_ASSET_PARAM);
    return urlParamToAsset(urlAsset);
  }, [searchParams]);

  const getGroupFromUrl = useCallback((): string | undefined => {
    const urlGroup = searchParams.get(PURCHASE_GROUP_PARAM);
    return urlGroup || undefined;
  }, [searchParams]);

  const [step, setStep] = useState<CartStep>(getStepFromUrl);
  const [asset, setAsset] = useState<OptionalAssets>();

  const populateForm = useCallback(
    (asset: OptionalAssets) => {
      if (cart?.[asset]?.items?.length) {
        form.setValue('currency', cart?.currency || SupportedCurrencies.USD);
        form.setValue('asset', asset);
      } else {
        form.setValue('currency', SupportedCurrencies.USD);
        form.setValue('asset', asset);
        form.setValue('useCredits', false);
      }

      if (cart?.billingInformation?.billingAddress) {
        form.setValue('billingAddress', cart.billingInformation.billingAddress);
      }
      if (cart?.billingInformation?.email) {
        form.setValue('userInfo.email', cart.billingInformation.email);
      }
      if (cart?.billingInformation?.phoneNumber) {
        form.setValue(
          'userInfo.phoneNumber',
          cart.billingInformation.phoneNumber,
        );
      }
      if (cart?.billingInformation?.firstName) {
        form.setValue('userInfo.firstName', cart.billingInformation.firstName);
      }
      if (cart?.billingInformation?.lastName) {
        form.setValue('userInfo.lastName', cart.billingInformation.lastName);
      }
      if (cart?.billingInformation?.countryCode) {
        form.setValue(
          'userInfo.countryCode',
          cart.billingInformation.countryCode || '',
        );
      }
    },
    [cart, form],
  );

  const validateStepAndAsset = useCallback(
    (
      step: CartStep,
      asset?: OptionalAssets,
    ): { step: CartStep; asset?: OptionalAssets } => {
      if (stepRequiresAsset(step) && !asset) {
        return { step: CartStep.Assets, asset: undefined };
      }

      if (isBillingStep(step)) {
        // Skip cart-items check for anonymous users — the cart was just
        // prepared by the quick purchase flow and may not be in the React Query
        // cache yet when the purchase page first mounts.
        const isAnonymous = Boolean(userStore.currentUser?.isAnonymous);
        if (!isAnonymous) {
          const cartItems = cart?.[asset as OptionalAssets]?.items?.length;
          if (!cartItems) {
            return { step: CartStep.Assets, asset: undefined };
          }
        }
      }

      if (!validateQuickBuy(step, asset)) {
        return { step: CartStep.Assets, asset: undefined };
      }
      return { step, asset };
    },
    [cart, isCartLoading, userStore.currentUser?.isAnonymous],
  );

  const updateUrl = useCallback(
    (newStep: CartStep, newAsset?: OptionalAssets | null) => {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set(PURCHASE_STEP_PARAM, newStep);

      if (newAsset) {
        currentParams.set(PURCHASE_ASSET_PARAM, newAsset);
      } else {
        currentParams.delete(PURCHASE_ASSET_PARAM);
      }

      setSearchParams(currentParams, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (isCartLoading) {
      return;
    }
    const urlStep = getStepFromUrl();
    const urlAsset = getAssetFromUrl();
    const urlGroup = getGroupFromUrl();

    const validated = validateStepAndAsset(urlStep, urlAsset);

    if (validated.step !== urlStep || validated.asset !== urlAsset) {
      updateUrl(validated.step, validated.asset);
      setStep(validated.step);

      setAsset(validated.asset);
      form.setValue('asset', validated.asset);
    } else {
      setStep(urlStep);
      setAsset(urlAsset);
      form.setValue('asset', urlAsset);
    }

    if (validated.asset) {
      populateForm(validated.asset);
    }

    if (validated.step === CartStep.Programs && urlGroup) {
      const currentGroup = form.getValues('group');

      if (currentGroup !== urlGroup) {
        form.setValue('group', urlGroup);
        const currentParams = new URLSearchParams(searchParams);
        currentParams.delete(PURCHASE_GROUP_PARAM);
        setSearchParams(currentParams, { replace: true });
      }
    }
  }, [searchParams, isCartLoading]);

  // Handle browser popstate events (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      // Skip validation during cart loading
      const urlStep = getStepFromUrl();
      const urlAsset = getAssetFromUrl();
      const validated = validateStepAndAsset(urlStep, urlAsset);

      setStep(validated.step);
      setAsset(validated.asset);

      // If step was corrected, update URL
      if (validated.step !== urlStep || validated.asset !== urlAsset) {
        updateUrl(validated.step, validated.asset);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    searchParams,
    validateStepAndAsset,
    updateUrl,
    isCartLoading,
    getStepFromUrl,
    getAssetFromUrl,
  ]);

  const handleNextStep = useCallback(
    (newStep: CartStep, newAsset?: OptionalAssets) => {
      const targetAsset = newAsset || asset;
      const validated = validateStepAndAsset(newStep, targetAsset);

      setStep(validated.step);
      setAsset(validated.asset);
      updateStep(validated.step);
      updateUrl(validated.step, validated.asset);

      // Update form if asset is available
      if (validated.asset) {
        populateForm(validated.asset);
      }
    },
    [asset, validateStepAndAsset, updateStep, updateUrl, populateForm],
  );

  const trackBackClick = useCallback(
    (currentStep: CartStep, targetStep: CartStep | null) => {
      if (
        currentStep !== CartStep.Billing &&
        currentStep !== CartStep.Programs &&
        currentStep !== CartStep.Assets
      ) {
        return;
      }

      const currency = form.getValues('currency') || SupportedCurrencies.USD;
      const stepProperties = getBackClickStepProperties(
        cart,
        currentStep,
        asset,
        currency,
        currencies,
      );
      const trackingProps = getCheckoutTrackingProperties();

      const targetStepEventName =
        targetStep === null ? getEntryEventName() : stepToEventName[targetStep];

      const itemCount = cart?.[asset as OptionalAssets]?.items?.length ?? 0;
      handlePurchaseEvent(EventTypes.CHECKOUT_CLICKED_BACK, {
        current_step: stepToEventName[currentStep],
        target_step: targetStepEventName,
        checkout_session_id: trackingProps.checkoutSessionId,
        checkout_entry_path: trackingProps.checkoutEntryPath,
        checkout_context_state: getCheckoutContextState(itemCount),
        cart_items_count: itemCount,
        stepProperties,
      });
    },
    [asset, cart, currencies, form, handlePurchaseEvent],
  );

  const handlePreviousStep = useCallback(() => {
    if (isBackClickInProgress.current) {
      return;
    }
    isBackClickInProgress.current = true;

    if (backClickTimeoutRef.current) {
      clearTimeout(backClickTimeoutRef.current);
    }
    backClickTimeoutRef.current = setTimeout(() => {
      isBackClickInProgress.current = false;
      backClickTimeoutRef.current = null;
    }, BACK_CLICK_DEBOUNCE_MS);

    if (step === CartStep.Programs) {
      form.setValue('productId', '');
      form.setValue('group', '');
    }

    // Anonymous users (mid quick-purchase flow) cannot navigate back from assets step
    const isAnonymous = Boolean(userStore.currentUser?.isAnonymous);
    if (isAnonymous && step === CartStep.Assets) {
      isBackClickInProgress.current = false;
      return;
    }

    // If on Billing step with QuickBuy, use browser back to return to previous page (e.g., Orders)
    if (step === CartStep.Billing && asset === OptionalAssets.QuickBuy) {
      trackBackClick(step, null);
      navigate(-1);
    } else if (step === CartStep.Assets || asset === OptionalAssets.QuickBuy) {
      trackBackClick(step, null);
      navigate(PagePath.Overview);
    } else {
      const previousStep = backSteps[step];
      const targetAsset = previousStep === CartStep.Assets ? undefined : asset;

      trackBackClick(step, previousStep);

      const validated = validateStepAndAsset(previousStep, targetAsset);
      setStep(validated.step);
      setAsset(validated.asset);
      updateUrl(validated.step, validated.asset);
    }
  }, [
    step,
    asset,
    userStore.currentUser?.isAnonymous,
    navigate,
    validateStepAndAsset,
    updateUrl,
    form,
    trackBackClick,
  ]);

  return {
    step,
    asset,
    isLoading,
    handleNextStep,
    handlePreviousStep,
  };
};
